<template>
  <a-menu
      v-if="!loading"
      class="menu-profile"
      v-model:openKeys="openKeys"
      :selectedKeys="[]"
      mode="vertical"
      :items="items"
      @click="handleClick"
  />
  <a-modal
    v-model:visible="visibleProfile"
    :title="t('page.profile-edit-title')"
    :width="500"
    :footer="null"
  >
    <profile-form :isEdit="true" @close="visibleProfile = false" />
  </a-modal>
</template>
<script lang="ts" setup>
import { h, ref } from 'vue'
import type { MenuProps } from 'ant-design-vue'
import { Avatar } from 'ant-design-vue'
import { GlobalOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons-vue'
import { useAuthStore } from '@/store/auth-store'
import { useUserStore } from '@/store/user-store'
import { router } from '@/config/router/router'
import { Routes } from '@/config/router/routes'
import Language from '../Language.vue'
import { ProfileForm } from '../../profile'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const userStore = useUserStore()
const { profile, loading } = storeToRefs(userStore)

const authStore = useAuthStore()

const visibleProfile = ref(false)

const avatar = computed(() =>
  profile.value?.first_name && profile.value.last_name
    ? profile.value?.first_name[0].toLocaleUpperCase() +
      profile.value?.last_name[0].toLocaleUpperCase()
    : 'U',
)

const userName = computed(() =>
  profile.value?.first_name && profile.value.last_name
    ? `${profile.value?.first_name} ${profile.value?.last_name}`
    : t('menu.profile'),
)

const openKeys = ref<string[]>([])

const items = computed(() => [
  {
    key: 'profile-menu',
    icon: () =>
      h(
        Avatar,
        {
          style: { backgroundColor: '#F0F5FF', color: '#5C95FF', borderRadius: '10px' },
          shape: 'square',
        },
        [avatar.value],
      ),
    label: userName.value,
    title: userName.value,
    class: 'menu-profile-item',
    popupOffset: [0, -100],
    children: [
      {
        icon: () => h(UserOutlined),
        key: 'profile-edit',
        label: t('menu.profile-edit'),
        title: t('menu.profile-edit'),
      },
      import.meta.env.VITE_APP_LANG_UI === 'true'
        ? {
            icon: () => h(GlobalOutlined),
            key: 'languages',
            label: h(Language),
          }
        : false,
      {
        icon: () => h(UploadOutlined),
        key: 'logout',
        label: t('ui.logout'),
        title: t('ui.logout'),
      },
    ].filter((m) => m !== false),
  },
])

const handleClick: MenuProps['onClick'] = (menuInfo) => {
  if (menuInfo.key === 'logout') {
    authStore.clearAuthData()
    router.push(Routes.Login)
  }
  if (menuInfo.key === 'profile-edit') {
    visibleProfile.value = true
  }
}
</script>

<style lang="scss" scoped>
.menu-profile {
  width: 100%;
  border-inline-end: none !important;

  :deep(.ant-menu-vertical) {
    border: none;
  }
  :deep(.menu-profile-item) {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;

    & .ant-menu-submenu-title,
    & .ant-menu-submenu-label {
      display: flex;
      align-items: center;
      color: var(--gray_100);

      border-radius: 0 !important;
      &:hover {
        background-color: var(--gray_20);
      }

      &__selected {
        color: var(--gray_20);
      }

      &__icon {
        color: var(--brand_50);
        filter: brightness(0) saturate(100%) invert(33%) sepia(98%) saturate(1232%)
          hue-rotate(194deg) brightness(93%) contrast(101%);
      }

      &__left,
      &__right {
        display: flex;
        align-items: center;
      }

      &__label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: inherit; /* Наследуем цвет от родителя */
      }
    }
  }
}
</style>